<template>
  <div id="wrapper" class="flex h-screen overflow-hidden bg-gradient-to-r from-[#f6f9ff] to-[#f6f9ff] dark:from-gray-900 dark:to-gray-950">
    <!-- Mobile Overlay -->
    <div
      v-if="mobileDrawerOpen && isMobile"
      @click="closeMobileDrawer"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
    ></div>

    <!-- Sidebar -->
    <aside
      id="sidebar"
      role="navigation"
      aria-label="主导航"
      class="fixed h-screen overflow-y-auto overflow-x-hidden border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 transition-all duration-300 z-50"
      :class="[
        sidebarOpen ? 'w-60 translate-x-0' : 'w-16 lg:translate-x-0 -translate-x-full'
      ]"
    >
      <!-- Logo Section -->
      <div class=" py-6 dark:border-gray-700">
        <div
          class="flex items-center"
          :class="sidebarOpen ? 'px-5' : 'justify-center px-2'"
        >
          <div v-if="sidebarOpen" class="min-w-0 flex-1 text-center">
            <p class="truncate text-base font-bold text-gray-800 dark:text-gray-200">
              {{ authStore.tenant?.name || "租户控制台" }}
            </p>
            <p class="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
              租户专属管理后台
            </p>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="mt-3">
        <RouterLink
          v-for="item in visibleNavigation"
          :key="item.path"
          :to="item.path"
          class="flex items-center py-4 text-gray-600 transition-colors hover:bg-blue-100 dark:text-gray-200 dark:hover:bg-gray-700"
          :class="[
            sidebarOpen ? 'px-6' : 'px-4 justify-center',
            isActiveRoute(item.path)
              ? 'bg-indigo-50 font-bold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200'
              : ''
          ]"
          @click="handleNavClick"
        >
          <div class="w-8 flex justify-center">
            <component :is="item.icon" class="h-5 w-5" />
          </div>
          <span v-if="sidebarOpen" class="ml-3 text-base whitespace-nowrap">
            {{ item.label }}
          </span>
        </RouterLink>
      </nav>
    </aside>

    <!-- Main Content -->
    <div class="flex h-screen flex-1 flex-col overflow-hidden transition-all duration-300" :class="[sidebarOpen ? 'lg:ml-60' : 'lg:ml-16']">
      <!-- Header -->
      <header class="sticky top-0 z-30 h-[78px] shrink-0 border-b border-gray-200 bg-white/95 backdrop-blur dark:border-gray-700 dark:bg-gray-800/95">
        <div class="flex h-full items-center px-5">
          <div class="flex w-full items-center justify-between gap-4">
            <!-- Left: Hamburger Toggle -->
            <button
              @click="toggleSidebar"
              class="flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              :aria-label="sidebarOpen ? '收起侧边栏' : '展开侧边栏'"
              :aria-expanded="sidebarOpen"
              aria-controls="sidebar"
            >
              <Bars3Icon class="h-5 w-5" />
            </button>

            <!-- Right: Theme Toggle, Role Badge, Account Menu -->
            <div class="flex items-center gap-4">
              <button
                @click="toggleDarkMode"
                class="flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                :title="isDark ? '切换到浅色模式' : '切换到暗黑模式'"
              >
                <SunIcon v-if="isDark" class="h-5 w-5" />
                <MoonIcon v-else class="h-5 w-5" />
              </button>
              <span class="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                {{ roleLabel }}
              </span>
              <AccountMenu />
            </div>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div class="p-5">
          <router-view />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter, RouterLink } from "vue-router";
import {
  ChartBarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  SwatchIcon,
  UserGroupIcon,
  UsersIcon,
  MoonIcon,
  SunIcon,
  Bars3Icon,
  CurrencyDollarIcon,
} from "@heroicons/vue/24/outline";

import { getMeAPI } from "@/api/auth";
import { useToken } from "@/hooks/useToken";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useAuthStore } from "@/stores/auth";
import { TENANT_CONSOLE_ROLE_LABELS, type NavigationItem } from "@/types/tenantConsole";
import AccountMenu from "@/components/AccountMenu.vue";

type NavigationItemWithIcon = NavigationItem & {
  icon: typeof SwatchIcon;
};

const STORAGE_KEY = "tenant-console.sidebar.desktopExpanded";
const MOBILE_BREAKPOINT = 1024;

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { removeToken } = useToken();
const { isDark, toggleDarkMode, initDarkMode } = useDarkMode();

// State
const isMobile = ref(false);
const mobileDrawerOpen = ref(false);
const desktopExpanded = ref(true);

// Computed
const sidebarOpen = computed(() => {
  return isMobile.value ? mobileDrawerOpen.value : desktopExpanded.value;
});

const navigation: NavigationItemWithIcon[] = [
  { label: "数据概览", path: "/dashboard", icon: ChartBarIcon },
  { label: "品牌与功能", path: "/settings", icon: SwatchIcon },
  { label: "用户管理", path: "/users", icon: UsersIcon },
  { label: "团队成员", path: "/team-members", icon: UserGroupIcon },
  { label: "套餐销售配置", path: "/plan-configs", icon: CurrencyDollarIcon },
  { label: "交易记录", path: "/orders", icon: ClipboardDocumentListIcon },
  { label: "操作日志", path: "/audit", icon: DocumentTextIcon },
];

const logoUrl = computed(() => authStore.tenant?.logoLightUrl || authStore.tenant?.logoDarkUrl || "");
const visibleNavigation = computed(() => navigation.filter((item) => !item.ownerOnly || authStore.isOwner));
const roleLabel = computed(() => {
  if (!authStore.account) {
    return "未登录";
  }
  return TENANT_CONSOLE_ROLE_LABELS[authStore.account.role];
});

/**
 * 检查路由是否激活（支持前缀匹配）。
 */
const isActiveRoute = (path: string): boolean => {
  return route.path === path || route.path.startsWith(path + '/');
};

/**
 * 加载桌面侧边栏展开状态。
 */
const loadDesktopExpandedState = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
};

/**
 * 保存桌面侧边栏展开状态。
 */
const saveDesktopExpandedState = (expanded: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(expanded));
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * 切换侧边栏。
 */
const toggleSidebar = () => {
  if (isMobile.value) {
    mobileDrawerOpen.value = !mobileDrawerOpen.value;
  } else {
    desktopExpanded.value = !desktopExpanded.value;
    saveDesktopExpandedState(desktopExpanded.value);
  }
};

/**
 * 关闭移动端抽屉。
 */
const closeMobileDrawer = () => {
  if (isMobile.value) {
    mobileDrawerOpen.value = false;
  }
};

/**
 * 处理导航点击（移动端自动关闭）。
 */
const handleNavClick = () => {
  if (isMobile.value) {
    mobileDrawerOpen.value = false;
  }
};

/**
 * 锁定/解锁页面滚动。
 */
const lockBodyScroll = (lock: boolean) => {
  if (lock) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
};

/**
 * 处理 Esc 键关闭移动端抽屉。
 */
const handleEscKey = (event: KeyboardEvent) => {
  if (event.key === "Escape" && isMobile.value && mobileDrawerOpen.value) {
    closeMobileDrawer();
  }
};

/**
 * 处理窗口大小变化。
 */
let resizeTimer: number | null = null;
let wasMobile = false;

const handleResize = () => {
  if (resizeTimer) clearTimeout(resizeTimer);

  resizeTimer = setTimeout(() => {
    const nowMobile = window.innerWidth < MOBILE_BREAKPOINT;
    isMobile.value = nowMobile;

    // 断点切换时自动调整状态
    if (wasMobile !== nowMobile) {
      if (nowMobile) {
        // 切换到移动端：关闭抽屉
        mobileDrawerOpen.value = false;
      } else {
        // 切换到桌面端：恢复保存的展开状态
        desktopExpanded.value = loadDesktopExpandedState();
      }
    }

    wasMobile = nowMobile;
  }, 100);
};

/**
 * 同步当前租户控制台登录上下文。
 */
const hydrateMe = async () => {
  if (authStore.account && authStore.tenant) {
    return;
  }

  try {
    const response = await getMeAPI();
    if (response.status === 200 && response.data) {
      authStore.setAuthContext(response.data.account, response.data.tenant);
      return;
    }
  } catch {
    // 忽略异常并回退登录页。
  }

  removeToken();
  authStore.clearAuthContext();
  router.push("/login");
};

// Watch mobile drawer state to lock/unlock body scroll
watch(
  () => mobileDrawerOpen.value && isMobile.value,
  (shouldLock) => {
    lockBodyScroll(shouldLock);
  }
);

onMounted(() => {
  initDarkMode();
  hydrateMe();

  // 初始化响应式状态
  const nowMobile = window.innerWidth < MOBILE_BREAKPOINT;
  isMobile.value = nowMobile;
  wasMobile = nowMobile;

  if (nowMobile) {
    mobileDrawerOpen.value = false;
  } else {
    desktopExpanded.value = loadDesktopExpandedState();
  }

  // 添加事件监听
  window.addEventListener("resize", handleResize);
  window.addEventListener("keydown", handleEscKey);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("keydown", handleEscKey);
  if (resizeTimer) clearTimeout(resizeTimer);
  lockBodyScroll(false); // 确保清理滚动锁定
});
</script>
